import React, { useEffect, useRef, useState } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { css } from 'emotion';
import { Text } from '../shared-components/text';
import { CaretLeft } from 'phosphor-react';
import { PageDrawerToggle } from './shared/PageDrawerToggle';
import { Tooltip } from '@mui/material';
import { Colors } from '@be-brite/briteui';
import { scrollbar } from '../shared-components/shared-styles';

export default function MenuDrawer(props) {
  const [shouldRenderDrawer, setShouldRenderDrawer] = useState(props.open);
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(props.open);
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    if (props.open) {
      setShouldRenderDrawer(true);
      setTimeout(() => setIsAnimatingOpen(true), 10);
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    } else {
      setIsAnimatingOpen(false);
      closeTimeoutRef.current = setTimeout(() => {
        setShouldRenderDrawer(false);
      }, 400);
    }
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [props.open]);

  const toggleDrawer = (open, chosenPageIndex) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    props.setOpen(open);
    if (Number.isInteger(chosenPageIndex)) {
      props.setCurrentPageIndex(chosenPageIndex);
    }
  };

  const drawerContainer = css`
    position: fixed;
    top: 56px;
    left: 0;
    height: calc(100vh - 89px);
    z-index: 999;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
    ${isAnimatingOpen ? 'pointer-events: auto;' : ''}
    ${!shouldRenderDrawer ? 'display: none;' : ''}
  `;

  const list = css`
    width: 300px;
    height: 100%;
    border-right: 1px solid #d1dae3;
    position: relative;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateX(${isAnimatingOpen ? '0' : '-100%'});
    opacity: ${isAnimatingOpen ? '1' : '0'};
    pointer-events: ${isAnimatingOpen ? 'auto' : 'none'};
    background-color: white;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    isolation: isolate;
  `;

  const listPadding = css`
    padding: 8px 20px 8px 8px;
    flex: 1;
    overflow-y: auto;
    ${scrollbar.style}
  `;

  const listItem = css`
    padding-left: 32px;
    padding-bottom: 12px;
    padding-top: 12px;
    transition: background-color 0.2s ease;
    cursor: pointer;
    border-radius: 8px;
    &:hover {
      background-color: ${Colors.Gray200};
    }
  `;

  const activeListItem = css`
    background-color: ${Colors.Gray200};
    p {
      font-weight: 700;
    }
  `;

  const headerContainer = css`
    padding: 16px;
    padding-bottom: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
  `;

  return (
    <>
      {shouldRenderDrawer && (
        <div className={drawerContainer}>
          <div className={list} role="presentation">
            <div className={headerContainer}>
              <Text h4>{props.title ? props.title : 'Pages'}</Text>
            </div>

            <List className={listPadding}>
              {props.pages.map((page, index) => (
                <ListItem
                  className={`${listItem} ${index === props.currentPageIndex ? activeListItem : ''} page-list-nav ${
                    props.currentPageIndex - 1 === index
                      ? 'page-nav-prev'
                      : props.currentPageIndex + 1 === index
                      ? 'page-nav-next'
                      : ''
                  }`}
                  button
                  key={page.ID}
                  onClick={toggleDrawer(props.isDesktop ? props.open : false, index)}
                  onKeyDown={toggleDrawer(props.isDesktop ? props.open : false)}
                >
                  <Text label>{page.Name}</Text>
                </ListItem>
              ))}
            </List>
          </div>
          <div
            className={css`
              position: absolute;
              top: 5%;
              right: -12px;
              z-index: 1001;
              background: white;
              border: 1px solid #d1dae3;
              border-radius: 5px;
              padding: 2px 0;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
              display: flex;
              align-items: center;
              cursor: pointer;
              justify-content: center;
              opacity: ${isAnimatingOpen ? 1 : 0};
              pointer-events: ${isAnimatingOpen ? 'auto' : 'none'};
              transition: right 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s;
            `}
          >
            <Tooltip title="Hide pages" placement="top">
              <button
                tabIndex={0}
                aria-label={props.open ? 'Close pages menu' : 'Open pages menu'}
                onClick={() => props.setOpen(!props.open)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && props.setOpen(!props.open)}
                className="focus:outline-none"
                style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer' }}
              >
                <CaretLeft
                  size={24}
                  style={{
                    transition: 'transform 0.3s ease',
                    transform: isAnimatingOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                  }}
                />
              </button>
            </Tooltip>
          </div>
        </div>
      )}
      {!props.open && (
        <PageDrawerToggle
          pages={props.pages}
          Text
          setCurrentPageIndex={props.setCurrentPageIndex}
          currentPageIndex={props.currentPageIndex}
          setOpen={props.setOpen}
          open={props.open}
          designStyles={props.designStyles}
        />
      )}
    </>
  );
}
